import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Stack, Typography, InputBase, Button, IconButton, Chip, Skeleton, Menu, MenuItem} from '@mui/material';
import {useAtom} from 'jotai';
import {HugeiconsIcon} from '@hugeicons/react';
import {Search01Icon, Upload01Icon, SparklesIcon, File02Icon, Delete01Icon, Clock01Icon, Layers01Icon, SdCardIcon, MoreVerticalIcon} from '@hugeicons/core-free-icons';
import {toast} from 'react-toastify';
import {KnowledgeManagerRepository, mapDocument} from '../../../../repositories/KnowledgeManagerRepository';
import {ConfirmModalNew} from '../../mcp-discovery/components/ConfirmModalNew';
import {useStompWebsocketKbManager} from '../../../../hooks/useStompWebsocketKbManager';
import {useDebounce} from '../../../../hooks/useDebounce';
import {documentsAtom, documentsLoadingAtom} from '../atoms';
import {DocumentUploadModalNew} from './DocumentUploadModalNew';
import {DocumentPreviewModalNew} from './DocumentPreviewModalNew';
import {CaptureKnowledgeModalNew} from './CaptureKnowledgeModalNew';

const controlSx = {
  height: 40, display: "flex", alignItems: "center", gap: 1, px: 1.5, borderRadius: "12px", bgcolor: "#FAFAFD", color: "#8A8AA0", fontSize: 13,
  transition: "background-color 0.2s ease, color 0.2s ease",
  "&:hover": {bgcolor: "#F2F2F8"},
  "&:focus-within": {bgcolor: "rgba(132, 145, 247, 0.1)", color: "#4B55C9"},
};

const actionButtonSx = {
  height: 40, px: 2.25, borderRadius: "12px", textTransform: "none", fontWeight: 600, fontSize: 13, whiteSpace: "nowrap",
  color: "#374151", bgcolor: "#FAFAFD", transition: "background-color 0.2s ease",
  "&:hover": {bgcolor: "#ECECF3"},
  "& .MuiButton-startIcon": {mr: 0.75},
};

const formatSize =(bytes) => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
};

const statusStyle = (status) => {
  if (status === "indexed") return {color: '#2F9A6A', bg: 'rgba(47, 154, 106, 0.1)'};
  if (status === "processing" || status === "uploading") return {color: '#D9A13A', bg: 'rgba(217, 161, 58, 0.12)'};
  if (status === "failed") return {color: '#DC2626', bg: 'rgba(220, 38, 38, 0.1)'};
  return {color: '#8A8AA0', bg: '#F2F2F7'};
};

const TYPE_LABELS = {
  'application/pdf': 'PDF',
  'text/markdown': 'Markdown',
  'text/plain': 'Text',
  'text/csv': 'CSV',
  'application/json': 'JSON',
  'text/html': 'HTML',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
};

const docTypeLabel = (doc) => {
  if (doc.type && TYPE_LABELS[doc.type]) return TYPE_LABELS[doc.type];
  const ext = doc.name?.includes('.') ? doc.name.split('.').pop().toUpperCase() : null;
  return ext || 'File';
};

const hexToRgba = (hex, alpha) => {
  const clean = (hex || "#8491F7").replace("#", "");
  const full = clean.length === 3 ? clean.split("").map(c => c + c).join("") : clean;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Pastel tint and short label per kind of file, shown on the sheet
const FILE_KINDS = {
  PDF: {label: 'PDF', bg: '#FDECEC', color: '#D9707A'},
  Word: {label: 'DOC', bg: '#E6F0FA', color: '#6BA3D6'},
  Excel: {label: 'XLS', bg: '#E8F5EE', color: '#4FAE86'},
  CSV: {label: 'CSV', bg: '#E8F5EE', color: '#4FAE86'},
  Markdown: {label: 'MD', bg: '#EEF0FE', color: '#8491F7'},
  Text: {label: 'TXT', bg: '#EEF0FE', color: '#8491F7'},
  JSON: {label: 'JSON', bg: '#FBF1DF', color: '#D9A13A'},
  HTML: {label: 'HTML', bg: '#F9E9F3', color: '#D07AAE'},
};

const fileKind = (doc) => {
  const type = docTypeLabel(doc);
  return FILE_KINDS[type] || {label: type.slice(0, 4).toUpperCase(), bg: '#EEF0FE', color: '#8491F7'};
};

// Label of the file kind on its badge, and the small icon + value used in a card's facts row
const Fact = ({icon, children}) => (
  <Stack direction="row" alignItems="center" gap={0.6} sx={{minWidth: 0, color: '#8A8AA0'}}>
    <HugeiconsIcon icon={icon} size={14}/>
    <Typography noWrap sx={{fontSize: 12, color: '#6B6B80'}}>{children}</Typography>
  </Stack>
);

const Highlighted = ({text, term}) => {
  const words = term.split(/\s+/).filter(Boolean).map(word => word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  if (!words.length) return text;
  return text.split(new RegExp(`(${words.join("|")})`, "gi")).map((part, index) => index % 2
    ? <mark key={index} style={{background: "rgba(132, 145, 247, 0.28)", color: "#23264D", borderRadius: 4, padding: "0 2px"}}>{part}</mark>
    : part);
};

const ACTIVE_STATUSES =["uploading", "processing"];

const isCaptured = (doc) => (doc.name?.startsWith("captured-") && doc.name.endsWith(".md")) || doc.name?.startsWith("taught");

const SectionHeading = ({children, count}) => (
  <Typography sx={{
    gridColumn: '1 / -1', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
    color: '#8491F7', '&:not(:first-of-type)': {mt: 1.5},
  }}>
    {children} · {count}
  </Typography>
);

export const DocumentsTab = ({topic, onDocumentCountChanged}) => {
  const [documents, setDocuments] = useAtom(documentsAtom);
  const [loading, setLoading] = useAtom(documentsLoadingAtom);
  const [search, setSearch] = useState("");
  const [contentSearch, setContentSearch] = useState("");
  const [contentMatchIds, setContentMatchIds] = useState(null);
  const contentTerm = useDebounce(contentSearch, 500).trim();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionsMenu, setActionsMenu] = useState(null);
  const pollRef = useRef(null);

  const loadDocuments = useCallback(async (silent) => {
    if (!topic?.id) return;
    if (!silent) setLoading(true);
    try {
      const list = await KnowledgeManagerRepository.listDocuments(topic.id);
      setDocuments(list);
    } catch (err) {
      if (!silent) toast.error("Failed to load documents");
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [topic?.id, setDocuments, setLoading]);

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic?.id]);

  const topicDocuments = documents.filter(d => d.topicId === topic.id);

  const hasActive = topicDocuments.some(d => ACTIVE_STATUSES.includes(d.status));

  // Live status pushes while anything in this topic is still uploading/processing (e.g. a long
  // entity-extraction pass) - updates that document in place with zero REST calls, instead of
  // re-fetching the whole list on every change.
  const [wsUpdate] = useStompWebsocketKbManager(hasActive && topic?.id ? `/topic/topics/${topic.id}/documents` : null);
  useEffect(() => {
    if (!wsUpdate?.document) return;
    const updated = mapDocument(wsUpdate.document);
    setDocuments(prev => prev.some(d => d.id === updated.id) ? prev.map(d => d.id === updated.id ? updated : d) : prev);
  }, [wsUpdate, setDocuments]);

  // Slow fallback poll in case a push is ever missed (dropped connection, etc.) - the WS push above
  // is the primary path, so this just needs to eventually catch up, not be fast.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (hasActive) {
      pollRef.current = setInterval(() => loadDocuments(true), 30000);
    }
    return () => clearInterval(pollRef.current);
  }, [hasActive, loadDocuments]);

  useEffect(() => {
    if (!topic?.id || !contentTerm) {
      setContentMatchIds(null);
      return;
    }
    let cancelled = false;
    KnowledgeManagerRepository.searchDocumentsByContent(topic.id, contentTerm)
      .then(results => {
        if (!cancelled) setContentMatchIds(new Map(results.map(r => [r.documentId ?? r.document?.id ?? r.id, r.snippet ?? r.errorMessage])));
      })
      .catch(err => {
        if (cancelled) return;
        setContentMatchIds(null);
        toast.error("Failed to search document content");
        console.error(err);
      });
    return () => {cancelled = true;};
  }, [topic?.id, contentTerm]);

  const filteredDocs = topicDocuments.filter(d =>
    d.name?.toLowerCase().includes(search.toLowerCase()) && (!contentMatchIds || contentMatchIds.has(d.id)));
  const uploadedDocs = filteredDocs.filter(d => !isCaptured(d));
  const taughtDocs = filteredDocs.filter(isCaptured);
  const orderedDocs = [...uploadedDocs, ...taughtDocs];

  const handleUploaded = async () => {
    setUploadOpen(false);
    await loadDocuments();
    await onDocumentCountChanged?.();
  };

  const handleCaptured = async () => {
    setCaptureOpen(false);
    await loadDocuments();
    await onDocumentCountChanged?.();
  };

  const handleDelete = async () => {
    if (!deleteDoc) return;
    setDeleting(true);
    try {
      await KnowledgeManagerRepository.deleteDocument(deleteDoc.id);
      setDocuments(list => list.filter(d => d.id !== deleteDoc.id));
      toast.success("Document deleted");
      setDeleteDoc(null);
      await onDocumentCountChanged?.();
    } catch (err) {
      toast.error("Failed to delete document");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Stack gap={2}>
      <Stack direction={{xs: "column", sm: "row"}} gap={1.25} alignItems={{xs: "stretch", sm: "center"}} justifyContent="space-between">
        <Stack direction={{xs: "column", sm: "row"}} gap={1.25} sx={{flex: 1, minWidth: 0}}>
          <Box sx={{...controlSx, width: {xs: "100%", sm: 170, md: 220}, flexShrink: 0, boxSizing: "border-box"}}>
            <HugeiconsIcon icon={Search01Icon} size={16}/>
            <InputBase
              placeholder="Search by title..." value={search} onChange={(e) => setSearch(e.target.value)}
              inputProps={{"aria-label": "Search documents by title"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
            />
          </Box>
          <Box sx={{...controlSx, flex: 1, minWidth: 0, boxSizing: "border-box"}}>
            <HugeiconsIcon icon={File02Icon} size={16}/>
            <InputBase
              placeholder="Search by content..." value={contentSearch} onChange={(e) => setContentSearch(e.target.value)}
              inputProps={{"aria-label": "Search documents by content"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
            />
          </Box>
        </Stack>
        <Stack direction="row" gap={1.25} flexWrap="wrap" sx={{flexShrink: 0}}>
          <Button
            startIcon={<HugeiconsIcon icon={Upload01Icon} size={16}/>}
            onClick={() => setUploadOpen(true)}
            sx={actionButtonSx}
          >
            Upload
          </Button>
          <Button
            startIcon={<HugeiconsIcon icon={SparklesIcon} size={16}/>}
            onClick={() => setCaptureOpen(true)}
            sx={{...actionButtonSx, color: '#4B55C9', bgcolor: 'rgba(132, 145, 247, 0.14)', '&:hover': {bgcolor: 'rgba(132, 145, 247, 0.22)'}}}
          >
            Teach the Agent
          </Button>
        </Stack>
      </Stack>

      {loading && topicDocuments.length === 0 ? (
        <Stack gap={1}>
          {Array.from({length: 4}).map((_, index) => (
            <Skeleton key={index} height={54} sx={{borderRadius: '12px'}}/>
          ))}
        </Stack>
      ) : filteredDocs.length === 0 ? (
        <Box sx={{py: 6, textAlign: "center"}}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: hexToRgba(topic.color, 0.12), color: topic.color || '#8491F7', mx: "auto", mb: 1.5,
          }}>
            <HugeiconsIcon icon={File02Icon} size={22}/>
          </Box>
          <Typography sx={{fontSize: 14, fontWeight: 700, color: '#23264D'}}>
            {topicDocuments.length === 0 ? "No documents yet" : "No documents match"}
          </Typography>
          <Typography sx={{fontSize: 12, color: '#8A8AA0', mt: 0.5}}>
            {topicDocuments.length === 0 ? "Upload documents to build this topic's knowledge base" : "Try a different search"}
          </Typography>
        </Box>
      ) : (
        <Box sx={{display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))'}}>
          {orderedDocs.map(doc => {
            const style = statusStyle(doc.status);
            const kind = fileKind(doc);
            const working = ACTIVE_STATUSES.includes(doc.status);
            return (
              <React.Fragment key={doc.id}>
              {doc === uploadedDocs[0] && <SectionHeading count={uploadedDocs.length}>Uploaded documents</SectionHeading>}
              {doc === taughtDocs[0] && <SectionHeading count={taughtDocs.length}>Taught to the agent</SectionHeading>}
              <Box
                onClick={() => setPreviewDoc(doc)}
                sx={{
                  p: 2, borderRadius: '16px', bgcolor: '#FAFAFD', cursor: 'pointer', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5,
                  transition: 'transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease',
                  '&:hover': {bgcolor: '#F2F2F8', transform: 'translateY(-3px)'},
                }}
              >
                <Stack direction="row" alignItems="flex-start" gap={1.5}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '13px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', color: kind.color, bgcolor: kind.bg,
                  }}>
                    {kind.label}
                  </Box>
                  <Box sx={{flex: 1, minWidth: 0}}>
                    <Typography title={doc.name} sx={{
                      fontSize: 13.5, fontWeight: 600, color: '#23264D', lineHeight: 1.35,
                      display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere',
                    }}>
                      {doc.name}
                    </Typography>
                    <Chip
                      label={doc.status} size="small"
                      sx={{
                        mt: 0.75, height: 20, fontSize: 10, fontWeight: 700, color: style.color, bgcolor: style.bg,
                        ...(working && {animation: 'doc-pulse 1.6s ease-in-out infinite', '@keyframes doc-pulse': {'50%': {opacity: 0.55}}}),
                      }}
                    />
                  </Box>
                  <Box onClick={(e) => e.stopPropagation()} sx={{flexShrink: 0, mt: -0.5, mr: -0.75}}>
                    <IconButton
                      size="small" aria-label={`Actions for ${doc.name}`}
                      onClick={(e) => setActionsMenu({anchor: e.currentTarget, doc})}
                      sx={{color: '#8A8AA0', '&:hover': {bgcolor: 'rgba(132, 145, 247, 0.12)', color: '#4B55C9'}}}
                    >
                      <HugeiconsIcon icon={MoreVerticalIcon} size={16}/>
                    </IconButton>
                  </Box>
                </Stack>

                {contentMatchIds?.get(doc.id) && (
                  <Typography sx={{
                    px: 1.75, py: 1.5, borderRadius: '12px', bgcolor: 'rgba(132, 145, 247, 0.08)', fontSize: 11, color: '#6B6B80', lineHeight: 1.7,
                    display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', overflowWrap: 'anywhere',
                  }}>
                    <Highlighted text={String(contentMatchIds.get(doc.id))} term={contentTerm}/>
                  </Typography>
                )}

                <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                  <Fact icon={SdCardIcon}>{formatSize(doc.size)}</Fact>
                  <Fact icon={Layers01Icon}>{doc.chunks ? `${doc.chunks} chunks` : 'No chunks'}</Fact>
                  <Fact icon={Clock01Icon}>{formatDate(doc.uploadedAt) || '—'}</Fact>
                </Stack>
              </Box>
              </React.Fragment>
            );
          })}
        </Box>
      )}

      <Menu
        anchorEl={actionsMenu?.anchor}
        open={Boolean(actionsMenu)}
        onClose={() => setActionsMenu(null)}
        PaperProps={{sx: {borderRadius: '12px'}}}
      >
        <MenuItem
          onClick={() => {setPreviewDoc(actionsMenu.doc); setActionsMenu(null);}}
          sx={{gap: 1}}
        >
          <HugeiconsIcon icon={File02Icon} size={16}/><Typography sx={{fontSize: '13px'}}>View</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {setDeleteDoc(actionsMenu.doc); setActionsMenu(null);}}
          sx={{color: '#DC2626', gap: 1}}
        >
          <HugeiconsIcon icon={Delete01Icon} size={16}/><Typography sx={{fontSize: '13px'}}>Delete</Typography>
        </MenuItem>
      </Menu>

      <DocumentUploadModalNew topicId={topic.id} open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={handleUploaded}/>

      <CaptureKnowledgeModalNew topicId={topic.id} open={captureOpen} onClose={() => setCaptureOpen(false)} onCaptured={handleCaptured}/>

      <DocumentPreviewModalNew
        document={previewDoc} open={!!previewDoc} onClose={() => setPreviewDoc(null)}
        onUpdated={async (updated) => {
          setPreviewDoc(updated);
          await loadDocuments(true);
        }}
      />

      <ConfirmModalNew
        open={!!deleteDoc}
        onClose={() => setDeleteDoc(null)}
        onConfirm={handleDelete}
        title={`Delete "${deleteDoc?.name}"?`}
        description="This will remove the document from the knowledge base. This action cannot be undone."
        confirmText="Delete Document"
        loading={deleting}
      />
    </Stack>
  );
};
